import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { Elements } from '@stripe/react-stripe-js'
import config from '../../../../config/config.json'
import type { StripeElementLocale } from '@stripe/stripe-js'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { useTranslation } from 'react-i18next'
import CartGridContainer from '../../Cart/CartContent/CartGridContainer'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { EditLoader } from '../../UIData/_actions/UIDataActions'
import { CartCheckoutStripeGetPaymentIntent } from '../../UserData/_actions/UserCartActions'
import type Immutable from 'immutable'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import CheckoutPaymentContent from './CheckoutPaymentContent'
import stripePaymentElementAppearance, { stripePaymentElementFonts } from '../../Common/Form/StripePaymentElementAppearance'
import { cartHasValidCheckoutAddress } from '../CheckoutAddressValidation'
import getStripePromise from '../../Common/Form/StripeLoader'

type StripeLoadStatus = 'loading' | 'ready' | 'failed'

interface Props {
  orders: Immutable.OrderedMap<string, UserCartStore>
  confirmationUrl: string
}

export default function CheckoutPayment(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('cart')

  const loggedIn = useAppSelector(state => state.get('userData').get('loggedIn'))
  const countries = useAppSelector(state => state.get('appData').get('countries'))

  const [stripeClientSecret, setStripeClientSecret] = useState<string | undefined>(undefined)
  const [savePaymentMethod, setSavePaymentMethod] = useState(loggedIn)
  const [stripeError, setStripeError] = useState('')
  const [stripeLoadStatus, setStripeLoadStatus] = useState<StripeLoadStatus>('loading')
  const [stripeUpdate, setStripeUpdate] = useState(Date.now())
  const stripePromise = useMemo(getStripePromise, [])

  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  useEffect(() => {
    let active = true

    void stripePromise.then((stripe) => {
      if (!active) {
        return
      }

      setStripeLoadStatus(stripe ? 'ready' : 'failed')
    }, () => {
      if (!active) {
        return
      }

      setStripeLoadStatus('failed')
    })

    return () => {
      active = false
    }
  }, [stripePromise])

  const isFree = useMemo(() => {
    // Check if total is 0$
    let tmpIsFree = true

    props.orders.forEach((cart) => {
      if (cart.get('total').get('amount') > 0) {
        tmpIsFree = false
      }
    })

    return tmpIsFree
  }, [props.orders])

  const validBillingAddress = useMemo(() => {
    let valid = true
    props.orders.forEach(cart => {
      if (!cartHasValidCheckoutAddress(cart, 'billing', countries)) {
        valid = false
        return false
      }
    })
    return valid
  }, [props.orders, countries])

  // Re-trigger payment intent on every change to orders
  useEffect(() => {
    if (isFree) {
      return
    }

    if (!validBillingAddress) {
      setStripeError('')
      setStripeClientSecret(undefined)
      dispatch(EditLoader('payment', false))
      return
    }

    if (stripeLoadStatus !== 'ready') {
      setStripeClientSecret(undefined)
      setStripeError(stripeLoadStatus === 'failed' ? t('Card payments could not be loaded. Please refresh the page or use PayPal.') : '')
      dispatch(EditLoader('payment', false))
      return
    }

    setStripeError('')
    dispatch(EditLoader('payment', true))

    // Provide id order if we're paying an 'ordered' order
    const firstOrder = props.orders.count() === 1 ? props.orders.first() : undefined
    const idOrder = firstOrder?.get('status') === 'ordered' ? firstOrder.get('id') : undefined

    dispatch(CartCheckoutStripeGetPaymentIntent(savePaymentMethod, idOrder).set({
      onSuccess: (response) => {
        setStripeError('')
        setStripeUpdate(Date.now())
        setStripeClientSecret(response.data.secret)
      },
      onFailure: (response) => {
        // Reset loader payment loader here as it won't be reenabled downstream
        dispatch(EditLoader('payment', false))

        if (response.data?.error === 'invalid-shipping-address') {
          setStripeError(t('Your shipping address is invalid. Please edit and reconfirm your address.'))
        } else if (response.data?.error === 'invalid-billing-address') {
          setStripeError(t('Your billing address is invalid. Please edit reconfirm your address.'))
        } else {
          setStripeError(t('An error occured'))
        }
      }
    }))
  }, [dispatch, t, savePaymentMethod, props.orders, isFree, validBillingAddress, stripeLoadStatus])

  const firstCart = useMemo(() => {
    return props.orders.first()
  }, [props.orders])

  const currency = useMemo(() => {
    if (!firstCart) return 'USD'

    return firstCart.get('total').get('currency')
  }, [firstCart])

  return <CartGridContainer>
    {isFree ? <>
      <CheckoutPaymentContent
        orders={props.orders}
        paymentOption="free"
        savePaymentMethod={savePaymentMethod}
        setSavePaymentMethod={setSavePaymentMethod}
        confirmationUrl={props.confirmationUrl}
      />
    </> : (stripeClientSecret && stripeLoadStatus === 'ready' ? <Elements
      stripe={stripePromise}
      options={{
        clientSecret: stripeClientSecret,
        loader: 'auto',
        locale: window.LOCALE.replace('_', '-') as StripeElementLocale,
        fonts: stripePaymentElementFonts,
        appearance: stripePaymentElementAppearance
      }}
    >
      <PayPalScriptProvider
        options={{
          clientId: config.PAYPAL_CLIENT_ID,
          locale,
          currency,
          disableFunding: ['card', 'paylater'],
          intent: 'capture',
          dataPageType: 'checkout'
        }}
      >
        <CheckoutPaymentContent
          orders={props.orders}
          stripeReady
          stripeUpdate={stripeUpdate}
          onStripeLoadError={() => {
            setStripeClientSecret(undefined)
            setStripeLoadStatus('failed')
            setStripeError(t('Card payments could not be loaded. Please refresh the page or use PayPal.'))
          }}
          savePaymentMethod={savePaymentMethod}
          setSavePaymentMethod={setSavePaymentMethod}
          confirmationUrl={props.confirmationUrl}
        />
      </PayPalScriptProvider>
    </Elements> : <PayPalScriptProvider
      options={{
        clientId: config.PAYPAL_CLIENT_ID,
        locale,
        currency,
        disableFunding: ['card', 'paylater'],
        intent: 'capture',
        dataPageType: 'checkout'
      }}
    >
      <CheckoutPaymentContent
        orders={props.orders}
        stripeError={stripeError}
        savePaymentMethod={savePaymentMethod}
        setSavePaymentMethod={setSavePaymentMethod}
        confirmationUrl={props.confirmationUrl}
      />
    </PayPalScriptProvider>)}
      { /* <>
        <CartListGrid>
          <Skeleton
            variant="rounded"
            height={800}
          />
        </CartListGrid>
        <Grid item xs={12} md={4} lg={3}>
          <Skeleton
            variant="rounded"
            height={500}
          />
        </Grid>
    </> */ }
  </CartGridContainer>
}
